import { useMemo, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';

import { Permission } from '../../../../../../../../../../../backend/src/shared/action-registry';
import { ReferenceType } from '../../../../../../../../../../../backend/src/types/types';
import { useGetFactDetailsBatch } from '@/api/queries/useGetFactDetailsBatch';
import { PermissionGate } from '@/hooks/useHasPermission';
import { CopyIconButton } from '@/new/components/copy-icon-button';
import { DocChronologyDropdown } from '@/new/components/doc-chronology-dropdown';
import { FactAccordionHeader } from '@/new/components/fact/fact-accordion';
import { FactChronologyDropdown } from '@/new/components/fact-chronology-dropdown';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { ButtonGroup } from '@/new/components/shadcn/button-group';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

function FactReferences({
  matterId,
  subsetId,
  factIds,
  referenceIndexMap,
  onReferenceClick,
}: {
  matterId: string;
  subsetId: string;
  factIds: string[];
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
}) {
  const { isLoading, data } = useGetFactDetailsBatch(matterId, subsetId, factIds);
  const facts = data ?? [];

  const [selectedFactIds, setSelectedFactIds] = useState<Set<string>>(new Set());

  const docIds = useMemo(() => {
    const ids = new Set<string>();
    for (const fact of facts) {
      for (const location of fact.locations) {
        ids.add(location.docId);
      }
    }
    return Array.from(ids);
  }, [facts]);

  const allRowsChecked = facts.length > 0 && selectedFactIds.size === facts.length;

  const toggleAllHandler = () => {
    if (allRowsChecked) {
      setSelectedFactIds(new Set());
    } else {
      setSelectedFactIds(new Set(facts.map((f) => f.id)));
    }
  };

  return (
    <>
      {!isLoading && (
        <div className="mt-8 flex flex-row-reverse flex-wrap items-center justify-between gap-2 gap-y-4 pr-2 pl-5">
          <ButtonGroup className="flex grow justify-center">
            <Button size="sm" asChild>
              <Link to={`/matters/${matterId}/subsets/new?documentIds=${docIds.join(',')}`}>
                <Icon className="lucide--group" />
                Subset from Sources
              </Link>
            </Button>

            <FactChronologyDropdown
              matterId={matterId}
              subsetId={subsetId}
              selectedFactIds={selectedFactIds}
              triggerText="Fact Chronology"
              size="sm"
            />
            <DocChronologyDropdown
              matterId={matterId}
              subsetId={subsetId}
              selectedDocIds={new Set(docIds)}
              size="sm"
              triggerText="Document Chronology"
              createDescription="Create a new document chronology from the source documents referenced in this answer"
              addDescription="Add the source documents referenced in this answer to an existing document chronology"
            />
          </ButtonGroup>

          <div className="flex grow items-center gap-3 text-xs text-gray-500">
            <Checkbox checked={allRowsChecked} onCheckedChange={toggleAllHandler} />
            <p>
              <strong>{selectedFactIds.size.toLocaleString()}</strong> Facts selected
            </p>

            <Button
              size="sm"
              disabled={selectedFactIds.size === 0}
              onClick={() => setSelectedFactIds(new Set())}
              className="gap-1"
            >
              <Icon className="lucide--x" />
              Clear all
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2 px-1">
        {isLoading &&
          Array.from({ length: Math.min(factIds.length, 3) }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-xl bg-gray-100" />
          ))}

        {!isLoading &&
          facts.map((fact) => (
            <div key={fact.id} className="flex items-start gap-3">
              <button
                data-state="closed"
                className="group min-w-0 flex-1 cursor-pointer rounded-xl border bg-white px-4 py-3 pt-2.75 pr-3 text-left text-[13px] ring-offset-2 transition-colors duration-150 hover:border-gray-300 focus-visible:ring-1 focus-visible:ring-teal-500 focus-visible:outline-none"
                onClick={() => onReferenceClick?.(fact.id, ReferenceType.Fact)}
              >
                <FactAccordionHeader
                  selectable={{
                    isSelected: selectedFactIds.has(fact.id),
                    handleSelect: (checked) => {
                      setSelectedFactIds((prev) => {
                        const next = new Set(prev);
                        if (checked) {
                          next.add(fact.id);
                        } else {
                          next.delete(fact.id);
                        }
                        return next;
                      });
                    },
                  }}
                  reference={referenceIndexMap.get(fact.id)?.index}
                  onViewDocument={() => {
                    onReferenceClick?.(fact.id, ReferenceType.Fact);
                  }}
                  fact={fact}
                  subsetId={subsetId}
                />
              </button>
            </div>
          ))}
      </div>
    </>
  );
}

export function EndBlock({
  getTextToCopy,
  factIds,
  referenceIndexMap,
  onReferenceClick,
  onDownloadClick,
  isDownloading,
}: {
  getTextToCopy: () => string;
  factIds: string[];
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
  onDownloadClick?: () => void;
  isDownloading?: boolean;
}) {
  const { matterId, subsetId } = useParams();
  const [showFactReferences, setShowFactReferences] = useState(false);

  return (
    <div>
      <motion.div
        className="mx-auto mt-6 flex w-full max-w-3xl justify-between gap-4 px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15, delay: 0.2 }}
      >
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <CopyIconButton getText={getTextToCopy} />
            </TooltipTrigger>

            <TooltipContent>Copy answer to clipboard</TooltipContent>
          </Tooltip>

          <PermissionGate
            permission={Permission.ThreadDownload}
            uiMode="disabled"
            disabledMessage="You don't have permission to download an answer"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => onDownloadClick?.()}
                  disabled={isDownloading}
                  variant="ghost"
                  className="p-1 px-1.5"
                >
                  <Icon className="lucide--arrow-down-to-line" />
                </Button>
              </TooltipTrigger>

              <TooltipContent>Download as Word document</TooltipContent>
            </Tooltip>
          </PermissionGate>
        </div>

        <div>
          {factIds.length > 0 && (
            <Button
              variant={'ghost'}
              size={'sm'}
              className="group text-gray-600"
              onClick={() => setShowFactReferences((prev) => !prev)}
            >
              View references in answer
              <Icon className="lucide--book-open" />
              <span
                className={cn(
                  'inline-flex w-0 overflow-hidden transition-all duration-150 group-hover:w-5',
                  showFactReferences && 'w-5',
                )}
              >
                <Icon
                  className={cn(
                    'lucide--chevron-down shrink-0 duration-150 group-hover:opacity-100',
                    showFactReferences ? 'rotate-180 opacity-100' : 'opacity-0',
                  )}
                />
              </span>
            </Button>
          )}
        </div>
      </motion.div>

      <AnimatePresence>
        {showFactReferences && matterId && subsetId && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="mx-auto max-w-3xl overflow-hidden"
          >
            <FactReferences
              matterId={matterId}
              subsetId={subsetId}
              factIds={factIds}
              referenceIndexMap={referenceIndexMap}
              onReferenceClick={onReferenceClick}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
